import {
	createInertiaApp,
	type ResolvedComponent,
	router,
} from "@inertiajs/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

const queryClient = new QueryClient();

// Handle flash messages globally
router.on("flash", (event) => {
	const { notice, alert } = event.detail.flash;
	if (typeof notice === "string" && notice) {
		toast.success(notice);
	}
	if (typeof alert === "string" && alert) {
		toast.error(alert);
	}
});

void createInertiaApp({
	// Set default page title
	// see https://inertia-rails.dev/guide/title-and-meta
	//
	// title: title => title ? `${title} - App` : 'App',

	// Disable progress bar
	//
	// see https://inertia-rails.dev/guide/progress-indicators
	// progress: false,

	resolve: (name) => {
		const pages = import.meta.glob<{ default: ResolvedComponent }>(
			"../pages/**/*.tsx",
			{
				eager: true,
			},
		);
		const page = pages[`../pages/${name}.tsx`];
		if (!page) {
			console.error(`Missing Inertia page component: '${name}.tsx'`);
		}

		// To use a default layout, import the Layout component
		// and use the following line.
		// see https://inertia-rails.dev/guide/pages#default-layouts
		//
		// page.default.layout ||= (page: ReactNode) => (<Layout>{page}</Layout>)

		return page;
	},

	setup({ el, App, props }) {
		createRoot(el).render(
			<StrictMode>
				<QueryClientProvider client={queryClient}>
					<App {...props} />
					<Toaster />
				</QueryClientProvider>
			</StrictMode>,
		);
	},

	defaults: {
		form: {
			forceIndicesArrayFormatInFormData: false,
		},
		future: {
			useScriptElementForInitialPage: true,
			useDataInertiaHeadAttribute: true,
			useDialogForErrorModal: true,
			preserveEqualProps: true,
		},
	},
}).catch((error) => {
	// This ensures this entrypoint is only loaded on Inertia pages
	// by checking for the presence of the root element (#app by default).
	// Feel free to remove this `catch` if you don't need it.
	if (document.getElementById("app")) {
		throw error;
	} else {
		console.error(
			"Missing root element.\n\n" +
				"If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
				'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
		);
	}
});
